export type PermissionAction = "view" | "create" | "edit" | "delete" | "manage"

export interface PermissionActionConfig {
  action: PermissionAction
  label: string
  description?: string
  critical?: boolean
}

export interface PermissionModuleConfig {
  key: string
  label: string
  actions: PermissionActionConfig[]
  description?: string
}

export interface PermissionGroupConfig {
  key: string
  label: string
  description?: string
  modules: PermissionModuleConfig[]
}

export const CRUD_ACTIONS: PermissionActionConfig[] = [
  { action: "view", label: "View" },
  { action: "create", label: "Create" },
  { action: "edit", label: "Edit" },
  { action: "delete", label: "Delete", critical: true },
]

export const VIEW_ONLY_ACTION: PermissionActionConfig[] = [
  { action: "view", label: "View" },
]

export const MANAGE_ACTIONS: PermissionActionConfig[] = [
  { action: "view", label: "View" },
  { action: "create", label: "Create" },
  { action: "edit", label: "Edit" },
]

export const permissionGroups: PermissionGroupConfig[] = [
  {
    key: "administration",
    label: "Administration",
    modules: [
      {
        key: "accounts",
        label: "Account Management",
        description: "Manage admin, operator, and user accounts",
        actions: CRUD_ACTIONS,
      },
      {
        key: "feedbacks",
        label: "Feedback",
        actions: MANAGE_ACTIONS,
      },
      {
        key: "general",
        label: "General Data",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "video",
        label: "Video Categories",
        actions: MANAGE_ACTIONS,
      },
      {
        key: "giveaway",
        label: "Giveaway Images",
        actions: MANAGE_ACTIONS,
      },
      {
        key: "adminifyCalendar",
        label: "Adminify Calendar",
        actions: MANAGE_ACTIONS,
      },
    ],
  },
  {
    key: "recruiting",
    label: "Recruiting & Enablement",
    modules: [
      {
        key: "candidates",
        label: "Candidates",
        actions: CRUD_ACTIONS,
      },
      {
        key: "weekly",
        label: "Weekly Training",
        actions: CRUD_ACTIONS,
      },
      {
        key: "blitz",
        label: "Preseason Blitz",
        actions: CRUD_ACTIONS,
      },
      {
        key: "infoSession",
        label: "Info Sessions",
        actions: MANAGE_ACTIONS,
      },
      {
        key: "ecard",
        label: "Ecards & Interviews",
        actions: MANAGE_ACTIONS,
      },
      {
        key: "reps",
        label: "Signed Reps",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "employeeRoster",
        label: "Employee Roster",
        description: "Employee roster synced from Snowflake",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "myTrips",
        label: "My Trips",
        description: "Manage travel schedules, budgets, and participating reps",
        actions: CRUD_ACTIONS,
      },
      {
        key: "tripDetails",
        label: "Trip Expense Details",
        description: "View expense submissions for a specific trip",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "operationDetails",
        label: "Operation Details",
        description: "View operation receipts and details for a specific operation",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "receiptTransactions",
        label: "Receipt transactions",
        description:
          "View submitted receipts and update paid/unpaid status (assistants)",
        actions: [
          { action: "view", label: "View" },
          {
            action: "edit",
            label: "Payment status",
            description: "Mark individual receipts paid or unpaid",
          },
        ],
      },
      {
        key: "tools",
        label: "Recruiting Tools",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "breezy",
        label: "Breezy Data",
        actions: VIEW_ONLY_ACTION,
      },
    ],
  },
  {
    key: "leads",
    label: "Lead Management",
    modules: [
      {
        key: "leads",
        label: "Solar Leads",
        actions: CRUD_ACTIONS,
      },
      {
        key: "saleLeads",
        label: "Sale Lead Ads",
        actions: CRUD_ACTIONS,
      },
      {
        key: "appLeads",
        label: "App Leads",
        actions: CRUD_ACTIONS,
      },
      {
        key: "opportunities",
        label: "Opportunities",
        actions: CRUD_ACTIONS,
      },
      {
        key: "vivintLeads",
        label: "Vivint Leads",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "formLeads",
        label: "Form Leads",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "solar",
        label: "Solar Lead Management",
        actions: CRUD_ACTIONS,
      },
      {
        key: "solarOffices",
        label: "Solar Offices",
        actions: VIEW_ONLY_ACTION,
      },
    ],
  },
  {
    key: "dealer",
    label: "Dealer Operations",
    modules: [
      {
        key: "dealerCandidates",
        label: "Dealer Candidates",
        actions: CRUD_ACTIONS,
      },
      {
        key: "dealerContacts",
        label: "Dealer Contacts",
        actions: CRUD_ACTIONS,
      },
      {
        key: "dealerTrainings",
        label: "Dealer Trainings",
        actions: CRUD_ACTIONS,
      },
      {
        key: "dealerAds",
        label: "Dealer Ads",
        actions: CRUD_ACTIONS,
      },
      {
        key: "dealerCompanies",
        label: "Dealer Companies",
        actions: CRUD_ACTIONS,
      },
      {
        key: "locations",
        label: "Recruiter Offices",
        actions: CRUD_ACTIONS,
      },
    ],
  },
  {
    key: "vivint",
    label: "Vivint",
    modules: [
      {
        key: "mediaAds",
        label: "Vivint Ads & Weekly Trainings",
        description:
          "Vivint Ads (detail routes), exports, and Weekly Trainings under Vivint",
        actions: CRUD_ACTIONS,
      },
      {
        key: "vivintAdsPricing",
        label: "Vivint Ads Pricing",
        actions: CRUD_ACTIONS,
      },
      {
        key: "vivintCandidates",
        label: "Vivint Candidates",
        description: "Evolution / Vivint candidates list",
        actions: CRUD_ACTIONS,
      },
    ],
  },
  {
    key: "finance",
    label: "Finance & Compliance",
    modules: [
      {
        key: "reimbursement",
        label: "EVO Reimbursement",
        actions: CRUD_ACTIONS,
      },
      {
        key: "repReimbursement",
        label: "Rep Reimbursement",
        actions: CRUD_ACTIONS,
      },
      {
        key: "regionalReimbursement",
        label: "Regional Reimbursement",
        actions: CRUD_ACTIONS,
      },
      {
        key: "production",
        label: "Production",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "licenses",
        label: "Licenses",
        actions: MANAGE_ACTIONS,
      },
    ],
  },
  {
    key: "offices",
    label: "Office Operations",
    modules: [
      {
        key: "salesoffice",
        label: "Sales Offices",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "techoffice",
        label: "Tech Offices",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "campuses",
        label: "Campuses & ZIP Codes",
        actions: VIEW_ONLY_ACTION,
      },
      {
        key: "conctacts",
        label: "Contacts",
        actions: CRUD_ACTIONS,
      },
    ],
  },
  {
    key: "media",
    label: "Media & Marketing",
    modules: [
      {
        key: "ads",
        label: "Recruiting Media Ads",
        actions: CRUD_ACTIONS,
      },
      {
        key: "mediaStats",
        label: "Media Stats",
        actions: VIEW_ONLY_ACTION,
      },
    ],
  },
]

export const allPermissionModules = permissionGroups.flatMap(group =>
  group.modules.map(module => module.key)
)

